import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import { formatMeterDuration } from '../../common/commonUtils';

interface ChartDataItem {
  time: string;
  value: number;
  startTime: number;
}

interface MeterChartProps {
  title: string;
  data: ChartDataItem[];
  meterId: string;
  isDuration?: boolean;
  height?: number;
}

export const MeterChart: React.FC<MeterChartProps> = ({
  title,
  data,
  isDuration = false,
  height = 500,
}) => {
  const formatDurationForAxis = (seconds: number): string => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      return `${Math.round(seconds / 60)}m`;
    } else {
      return `${(seconds / 3600).toFixed(1)}h`;
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: '#1a237e', mb: 2 }}
      >
        {title}
      </Typography>
      <Box height={height} maxWidth={'500px'} width={'100%'}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="time"
              angle={-45}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 12, fill: '#666' }}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#666' }}
              tickFormatter={(value: number) =>
                isDuration ? formatDurationForAxis(value) : value.toString()
              }
            />
            <Tooltip
              labelFormatter={(label) => `Time: ${label}`}
              formatter={(value: number) => {
                if (isDuration) {
                  return [formatMeterDuration(value), title];
                }
                return [value, title];
              }}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              }}
            />
            <Bar
              dataKey="value"
              fill="#1a237e"
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
